import { TCommunityFeedPost, TPostCustomContent } from "@/types/post";
import { JSONContent } from "@tiptap/react";
import { clsx, type ClassValue } from "clsx";
import { customAlphabet } from "nanoid";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import {
	ACCEPTED_DOCUMENT_FILE_TYPES,
	ACCEPTED_IMAGE_FILE_TYPES,
	ASSETS_BASE_PREFIX,
	BASE_URL,
	MAX_IMAGE_FILE_SIZE,
	PRODUCTION_BASE_URL,
	PRODUCTION_HOSTNAME,
	ROLE_DISPLAY_NAMES,
	countryISOs,
} from "./constants";
import { TProductPrice } from "@/types/product-submission-v2";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

// Initially the images were stored with a full S3 URL inside the DB, we are moving away from that to just store the object path inside the db and add the BASE S3 URL from client to make it easy with switching between buckets. This function helps to load the existing urls while the migration to object path takes place
//
// TODO (zeekhuge): Instead of having this (method below) scattered around everywhere, we should use either of the following:
// 1: NextJS's custom image loader - https://nextjs.org/docs/app/api-reference/config/next-config-js/images
// 2: When you have a dedicated class/service for APIs, the S3 object-URL should be converted to normalized URL there itself.
export function formatImageUrl<T extends string | undefined | null>(
	url?: T
): T extends string ? string : undefined {
	if (url) {
		return (
			url.startsWith("data:image/png;base64") ||
			url.startsWith("https://") ||
			url.startsWith("http://")
				? url
				: `${ASSETS_BASE_PREFIX}/${url}`
		) as T extends string ? string : undefined;
	} else {
		return undefined as T extends string ? string : undefined;
	}
}

// Does the opposite of formatImageUrl when need to generate an objectKey, if url return path extracted from url, else return the path
export function getPathFromUrl(url?: string | null) {
	return url
		? url.startsWith("https://") || url.startsWith("http://")
			? url.split("/").slice(3).join("/")
			: url
		: undefined;
}

// Formats a URL to follow this scheme: https://<resource-address>
export function formatURL(url?: string | null) {
	if (url) {
		if (!url.startsWith("http://") && !url.startsWith("https://")) {
			return `https://${url}`;
		} else {
			return url;
		}
	} else {
		return undefined;
	}
}

export function linkToVideoEmbedURL(url: string) {
	return formatYoutubeLink(url) ?? formatVimeoLink(url);
}

// Formats a youtube link
export function formatYoutubeLink(url?: string) {
	if (!url || !isYoutubeUrl(url)) {
		return undefined;
	}

	try {
		const parsedUrl = new URL(url);
		let videoId = "";

		// Handle YouTube's various URL types - short-links, full-links, embed-links, and shorts
		if (parsedUrl.hostname.includes("youtu.be")) {
			videoId = parsedUrl.pathname.slice(1);
		} else if (parsedUrl.hostname.includes("youtube.com")) {
			if (parsedUrl.pathname === "/watch") {
				videoId = parsedUrl.searchParams.get("v") || "";
			} else if (parsedUrl.pathname.startsWith("/embed/")) {
				videoId = parsedUrl.pathname.split("/")[2];
			} else if (parsedUrl.pathname.startsWith("/shorts/")) {
				videoId = parsedUrl.pathname.split("/")[2];
			} else if (parsedUrl.pathname.startsWith("/live/")) {
				videoId = parsedUrl.pathname.split("/")[2];
			}
		}

		if (videoId) {
			return `https://www.youtube-nocookie.com/embed/${videoId}?controls=0`;
		}
	} catch (error) {
		console.error("Invalid YouTube URL:", error);
	}

	return undefined;
}

// Formats a vimeo link
export function formatVimeoLink(url?: string) {
	if (!url || !isVimeoUrl(url)) return undefined;

	const parts = url.split("/") || [""];
	const videoId = parts[parts?.length - 1];

	if (videoId) {
		return `https://player.vimeo.com/video/${videoId}`;
	} else {
		return undefined;
	}
}

export const randomNumberGenerator = customAlphabet("1234567890", 8);

// Formats views in K or M terms
export function formatViews(views?: number) {
	if (!views) {
		return "0";
	}

	if (views >= 1000000) {
		const millions = views / 1000000;
		const formatted = millions.toFixed(2);
		return formatted.replace(/\.?0+$/, "") + "M";
	} else if (views >= 1000) {
		return (views / 1000).toFixed(1) + "K";
	}

	return views.toString();
}

// Generates a temporary username
export function generateUsername(name?: string | null) {
	if (!name) {
		const randomNumber = randomNumberGenerator();

		return randomNumber;
	}

	const sanitized = name
		.toLowerCase()
		.replace(/[^\w\s]/g, "")
		.replace(/\s/g, "-");

	const randomSuffix = randomNumberGenerator(6);

	return `${sanitized}-${randomSuffix}`;
}

// Handle file validation during a form submission
export const fileValidation = ({
	isRequired = true,
	acceptedFileTypes = ACCEPTED_IMAGE_FILE_TYPES,
	maxFileSize = MAX_IMAGE_FILE_SIZE,
}: {
	isRequired?: boolean;
	acceptedFileTypes?: string[];
	maxFileSize?: number;
}) =>
	z.any().superRefine((files: FileList, ctx) => {
		if (!isRequired) {
			return;
		}

		if (!files || files?.length === 0) {
			return ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "File is required",
			});
		}

		Object.values(files).forEach((file) => {
			// TODO (zeekhuge): acceptedFileTypes & ACCEPTED_IMAGE_FILE_TYPES is not the same source
			if (!acceptedFileTypes.includes(file.type)) {
				const fileTypesForMessage = !ACCEPTED_IMAGE_FILE_TYPES.includes(file.type)
					? ".jpg, .jpeg, .png and .webp"
					: !ACCEPTED_DOCUMENT_FILE_TYPES.includes(file.type)
					? ".pdf"
					: "";

				return ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: `Only ${fileTypesForMessage} files are accepted.`,
				});
			}

			if (file.size >= maxFileSize) {
				const fileSizeForMessageInMB = maxFileSize / 1000000;

				return ctx.addIssue({
					code: z.ZodIssueCode.too_big,
					type: "number",
					inclusive: true,
					maximum: maxFileSize,
					message: `Max file size is ${fileSizeForMessageInMB}MB.`,
				});
			}
		});
	});

export const handleTrackClickEvent = (target_url: string, link_text: string) => {
	if (typeof window !== undefined) {
		window.rudderanalytics?.track("click", {
			target_url,
			link_text,
		});
	}
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => any>(func: T, delay: number) {
	let timeoutId: ReturnType<typeof setTimeout>;

	return (...args: Parameters<T>) => {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => func(...args), delay);
	};
}

/**
 * Sanitizes a filename by replacing spaces and special characters with dashes,
 * removing consecutive dashes, and converting the filename to lowercase
 *
 * @param filename - The filename to sanitize
 * @returns The sanitized filename
 */
export const sanitizeFilename = (filename: string) => {
	return filename
		.replace(/\s+/g, "-")
		.replace(/[^a-zA-Z0-9.-]/g, "-")
		.replace(/-+/g, "-")
		.replace(/^-+|-+$/g, "")
		.toLowerCase();
};

/**
 * Appends a timestamp to a give filename
 *
 * @param fileName - The name of the file to append the timestamp to
 * @returns The new filename with the timestamp appended
 */
export const appendTimestampToFilename = (fileName: string) => {
	const timestamp = Date.now();

	const dotIndex = fileName.lastIndexOf(".");
	const name = fileName.substring(0, dotIndex);
	const extension = fileName.substring(dotIndex);

	const newFileName = `${name}-${timestamp}${extension}`;

	return newFileName;
};

/**
 * Retrieves the ISO code for a given country location
 * @param location - The country location
 * @returns The ISO code for the country location, or null if the location is falsy
 */
export function getCountryIso(location: string | null) {
	if (!location) {
		return null;
	}

	return countryISOs?.[location as keyof typeof countryISOs];
}

/**
 * Checks if the current environment is production
 * @param hostname - The hostname to check
 * @returns True if the hostname is the production hostname, false otherwise
 */
export function isProduction(hostname?: string): boolean {
	if (!hostname) {
		return process.env.NEXT_PUBLIC_BASE_URL === PRODUCTION_BASE_URL;
	}

	return hostname === PRODUCTION_HOSTNAME;
}

/***
 * Gets pagination info from the URL search params
 * @param {URLSearchParams} searchParams - The URL search params
 * @param {number} maxPage - The maximum page number
 * @param {number} maxPerPage - The maximum number of items per page
 * @returns {object} cleansedPage: The cleaned page number
 * @returns {object} cleansedPerPage: The cleaned number of items per page
 */
export function getPaginationInfo({
	searchParams,
	maxPage,
	maxPerPage,
}: {
	searchParams: URLSearchParams;
	maxPage: number;
	maxPerPage: number;
}) {
	const page = searchParams.get("page") ?? "1";
	const perPage = searchParams.get("perPage") ?? "100";
	const parsedPage = parseInt(page) || 1;
	const parsedPerPage = parseInt(perPage) || 10;

	const cleansedPage = Math.max(1, Math.min(parsedPage, maxPage));
	const cleansedPerPage = Math.max(1, Math.min(parsedPerPage, maxPerPage));

	return { page: cleansedPage, perPage: cleansedPerPage };
}

/***
 * Checks if the given search parameter is a link from proptechbuzz.com
 * @param {string} searchParam - The search parameter to check
 * @returns {boolean} - True if the search parameter is a link from proptechbuzz.com, false otherwise
 */
export function isLinkFromDomain(searchParam: string) {
	try {
		const domain = PRODUCTION_HOSTNAME;

		const url = new URL(searchParam);

		// Normalize both the URL's hostname and the domain
		const normalizedHostname = url.hostname.replace(/^www\./, "");
		const normalizedDomain = domain.replace(/^www\./, "");

		// Check if the normalized hostname matches the normalized domain
		return normalizedHostname === normalizedDomain;
	} catch (error) {
		console.error("Invalid URL:", error);

		return false;
	}
}

/**
 * Constructs the post link for a given community, channel, and post slug
 * @param {communitySlug: string, channelSlug: string, postSlug: string}
 * @returns {string}
 */
export function constructPostLink({
	communitySlug,
	channelSlug,
	postSlug,
}: {
	communitySlug: string;
	channelSlug: string;
	postSlug: string;
}) {
	return `${BASE_URL}/communities/${communitySlug}/${channelSlug}/${postSlug}`;
}

/**
 * Constructs the comment link for a given community, channel, post and comment slug
 * @param {communitySlug: string, channelSlug: string, postSlug: string, commentSlug: string}
 * @returns {string}
 */
export function constructCommentLink({
	communitySlug,
	channelSlug,
	postSlug,
	commentSlug,
}: {
	communitySlug?: string;
	channelSlug?: string;
	postSlug: string;
	commentSlug: string;
}) {
	if (communitySlug && channelSlug) {
		return `${BASE_URL}/communities/${communitySlug}/${channelSlug}/${postSlug}#comment-${commentSlug}`;
	}

	return `${BASE_URL}/posts/${postSlug}#comment-${commentSlug}`;
}

export function constructProductCommentLink({
	productSlug,
	commentSlug,
}: {
	productSlug: string;
	commentSlug: string;
}) {
	return `${BASE_URL}/products/${productSlug}#comment-${commentSlug}`;
}

/**
 * Converts a version string to a number
 * @param version The version string to convert
 */
export function versionToNumber(version: string): number {
	// Split the version into main version and build metadata
	const [mainVersion, buildMetadata] = version.split("+");
	// Remove dots from the main version and concatenate with the build metadata
	const versionNumberString = mainVersion.replace(/\./g, "") + (buildMetadata || "");
	// Convert the result to a number
	return parseInt(versionNumberString, 10);
}

/**
 * Performs a deep equality check between two values, including nested objects and arrays

 * @param obj1 - The first value to compare
 * @param obj2 - The second value to compare
 * @returns `true` if the two values are deeply equal, `false` otherwise
 *
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function deepEqual(obj1: any, obj2: any): boolean {
	if (obj1 === obj2) {
		return true; // Handles primitives and reference equality
	}

	if (typeof obj1 !== "object" || obj1 === null || typeof obj2 !== "object" || obj2 === null) {
		return false; // Handles null and non-objects
	}

	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);

	if (keys1.length !== keys2.length) {
		return false; // If the number of properties differs
	}

	for (const key of keys1) {
		if (!keys2.includes(key)) {
			return false; // Keys must match
		}
		if (!deepEqual(obj1[key], obj2[key])) {
			return false; // Recursively check nested properties
		}
	}

	return true;
}

/**
 * Gets the readable role names for a given list of roles
 * @param roles - The list of roles
 * @returns The readable role names
 */
export function getReadableRoleNames(roles: string[]): string[] {
	return roles.map((role) => ROLE_DISPLAY_NAMES[role] || "Unknown Role");
}

/**
 * Formats product price for preview
 * @param price - The price input
 * @returns The pricing string
 */
export function formatProductPrice({ price }: { price: TProductPrice }) {
	if (price.free) {
		return "Free";
	} else if (price.contactUs) {
		return "Contact Us";
	} else {
		const from = price.priceRange?.from;
		const to = price.priceRange?.to;

		const currency = price.priceRange?.currency || "";

		if (from && !to) {
			return `${from} ${currency} onwards`;
		} else if (!from && to) {
			return `Until ${to} ${currency}`;
		} else if (from && to) {
			return `${from} - ${to} ${currency}`;
		} else {
			return "Invalid Price";
		}
	}
}

/*
 * Formats a price to a readable format
 * @param price - The price to format
 * @param currency - The currency to format the price in
 * @returns The formatted price
 */
export const formatPrice = (price: number, currency: string) => {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currency,
		minimumFractionDigits: 0,
	}).format(price);
};

/**
 * Checks if the given URL is a YouTube URL
 * @param url - The URL to check
 * @returns True if the URL is a YouTube URL, false otherwise
 */
export function isYoutubeUrl(url: string) {
	// Check if the URL is from YouTube or Vimeo
	const isYouTube = /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)/i.test(url);

	return isYouTube;
}

/**
 * Checks if the given URL is a Vimeo URL
 * @param url - The URL to check
 * @returns True if the URL is a Vimeo URL, false otherwise
 */
export function isVimeoUrl(url: string) {
	const vimeoRegex = /^(https?:\/\/)?(www\.)?vimeo\.com\/(\d+)(\/.*)?$/;

	return vimeoRegex.test(url);
}

/**
 * Checks if the given URL is a LinkedIn profile URL (person or company)
 * @param url - The URL to check
 * @returns True if the URL is a LinkedIn profile URL, false otherwise
 */
export function isLinkedInUrl(url: string) {
	// Matches LinkedIn profile or company URLs securely and case-insensitively
	const linkedInDomainRegex =
		/^https?:\/\/(www\.)?linkedin\.com\/(in|pub|company|school)\/[a-zA-Z0-9\-_%]+\/?$/i;
	return linkedInDomainRegex.test(url);
}

/**
 * Checks if the given URL is a valid URL
 * @param url - The URL to check
 * @returns True if the URL is a valid URL, false otherwise
 */
export function isValidUrl(url: string) {
	try {
		new URL(url);

		return true;
	} catch (error) {
		return false;
	}
}

/**
 * Parses a post JSON string
 * @param json - The JSON string to parse
 * @returns The parsed JSON string
 */
export function parsePostContentJSON(
	post?: (Omit<TCommunityFeedPost, "content"> & { content: string | null }) | null
): TCommunityFeedPost | null {
	if (!post) return null;

	if (!post.content) {
		return {
			...post,
			content: {},
		};
	}

	try {
		return {
			...post,
			content: JSON.parse(post.content),
		};
	} catch (error) {
		return {
			...post,
			content: {},
		};
	}
}

/**
 */
export function deleteNodeFromJsonContent(props: {
	jsonContent: JSONContent;
	deleteNode: (node: JSONContent) => boolean;
}): JSONContent | null {
	const jsonContent: JSONContent | null = { ...props.jsonContent };

	if (jsonContent.content && jsonContent.content.length !== 0) {
		jsonContent.content = jsonContent.content
			.map((e) => deleteNodeFromJsonContent({ jsonContent: e, deleteNode: props.deleteNode }))
			.filter((e) => !!e)
			.map((e) => e as JSONContent);
	}
	return props.deleteNode(jsonContent) ? null : jsonContent;
}

export function iterateOverNodeFromJsonContent(props: {
	jsonContent: JSONContent;
	func: (node: JSONContent) => void;
}): void {
	if (props.jsonContent.content && props.jsonContent.content.length !== 0) {
		props.jsonContent.content.forEach((e) =>
			iterateOverNodeFromJsonContent({ jsonContent: e, func: props.func })
		);
	}
	props.func(props.jsonContent);
}

/**
 * Parses a post custom content JSON string
 * @param customContent - The custom content JSON string to parse
 * @returns The parsed custom content
 */
export function parsePostCustomContent(
	customContent: string | undefined | null
): TPostCustomContent {
	const defaultResult = {
		images: [],
		links: [],
		imageViewType: "carousel" as "carousel" | "grid",
	};

	if (!customContent) {
		return defaultResult;
	}

	try {
		return {
			...defaultResult,
			...JSON.parse(customContent),
		};
	} catch (error) {
		return defaultResult;
	}
}

/**
 * Splits a full name into first and last name.
 * If only one name is provided, it is treated as the first name.
 *
 * @param fullName - The full name as a string.
 * @returns An object containing `firstName` and optionally `lastName`.
 */
export function getFirstAndLastName(fullName: string): { firstName: string; lastName?: string } {
	const [firstName, ...lastNameParts] = fullName.trim().split(" ");
	const lastName = lastNameParts.length ? lastNameParts.join(" ") : undefined;
	return { firstName, lastName };
}

/**
 * Combines first and last names into a full name.
 * If no last name is provided, it returns only the first name.
 *
 * @param firstName - The first name as a string.
 * @param lastName - (Optional) The last name as a string.
 * @returns The full name as a single string.
 */
export function combineFirstAndLastName(firstName: string, lastName?: string): string {
	return lastName ? `${firstName} ${lastName}` : firstName;
}

/**
 * Validates the image values uploaded from the product form.
 *
 * @param imageUrl - The logo URL.
 *
 * @returns true if valid, false if not.
 */
export const validateProductFormImageUrl = (imageUrl: string | undefined) => {
	if (!imageUrl) {
		return false;
	} else if (imageUrl == "") {
		return false;
	} else if (imageUrl == "company-logo-url") {
		return false;
	} else if (imageUrl == "product-logo-url") {
		return false;
	} else if (imageUrl.includes("screenshot")) {
		return false;
	} else {
		return true;
	}
};

/**
 * Parses a date to a Date object

 * @param date - The date to parse

 * @returns parsed date
 */

export const parseToDate = (date: Date | string | undefined): Date => {
	if (!date) {
		throw new Error("Invalid date value");
	}
	return typeof date === "string" ? new Date(date) : date;
};

// Gets timezones
export const getTimeZones = () => {
	const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

	let timezoneIdentifiers: string[] = [];

	try {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const { supportedValuesOf } = Intl as any;
		if (typeof supportedValuesOf === "function") {
			timezoneIdentifiers = supportedValuesOf("timeZone");
		}
	} catch (e) {
		timezoneIdentifiers = [
			"Pacific/Midway",
			"Pacific/Honolulu",
			"America/Anchorage",
			"America/Los_Angeles",
			"America/Denver",
			"America/Chicago",
			"America/New_York",
			"America/Caracas",
			"America/Sao_Paulo",
			"Atlantic/Azores",
			"Europe/London",
			"Europe/Paris",
			"Europe/Moscow",
			"Asia/Dubai",
			"Asia/Kolkata",
			"Asia/Kathmandu",
			"Asia/Dhaka",
			"Asia/Bangkok",
			"Asia/Shanghai",
			"Asia/Tokyo",
			"Australia/Sydney",
			"Pacific/Auckland",
		];
	}

	const now = new Date();

	return timezoneIdentifiers
		.map((zone) => {
			try {
				const formatter = new Intl.DateTimeFormat("en-GB", {
					timeZone: zone,
					timeZoneName: "longOffset",
				});
				const parts = formatter.formatToParts(now);
				const timeZonePart = parts.find((part) => part.type === "timeZoneName");

				const offsetString = timeZonePart?.value || "";

				const city = zone.split("/").pop()?.replace(/_/g, " ") || zone;

				return {
					value: zone,
					label: `${offsetString} ${city}`,
					gmtOffset: offsetString,
					city: city,
					isUserTimezone: zone === userTimeZone,
				};
			} catch (e) {
				return {
					value: zone,
					label: zone,
					gmtOffset: "",
					city: zone.split("/").pop()?.replace(/_/g, " ") || zone,
					isUserTimezone: zone === userTimeZone,
				};
			}
		})
		.sort((a, b) => {
			const getOffsetValue = (gmtOffset: string) => {
				const match = gmtOffset.match(/GMT([+-]\d{1,2}:\d{2})/);
				if (!match) return 0;

				const offsetStr = match[1];
				const sign = offsetStr.startsWith("-") ? -1 : 1;
				const [hours, minutes] = offsetStr.substring(1).split(":").map(Number);
				return sign * (hours * 60 + minutes);
			};

			const offsetA = getOffsetValue(a.gmtOffset);
			const offsetB = getOffsetValue(b.gmtOffset);

			if (offsetA !== offsetB) return offsetA - offsetB;

			return a.label.localeCompare(b.label);
		});
};

// Converts event time according to timezone
export const convertEventTime = (dateTime: string, selectedTimezone: string) => {
	const eventTime = new Date(dateTime);
	const utcTime = eventTime.getTime() + eventTime.getTimezoneOffset() * 60000;
	const offset = new Date(utcTime).toLocaleString("en-US", { timeZone: selectedTimezone });
	return new Date(offset);
};

export const getProductV2Path = (productSlug: string) => {
	return `/products/${productSlug}`;
};
